import PlatformLinks from "@/components/PlatformLinks";
import SEO from "@/components/SEO";
import CtaBanner from "@/components/CtaBanner";
import { MARKETPLACES } from "@/data/content";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";

const DETAILS = {
  Amazon: {
    badge: "₹1.4L Cr+ GMV",
    why: "India's #1 marketplace by GMV. Brand Registry, FBA and Sponsored Ads are non-negotiables for serious D2C brands.",
    bullets: ["Brand Registry approval", "FBA strategy & inbound", "Sponsored Products + Brands", "A+ Content & Stores"],
  },
  Flipkart: {
    badge: "40% market share",
    why: "Massive tier-1/2/3 reach. F-Assured badge unlocks 4x impressions. Fashion, mobiles and home are the heaviest categories.",
    bullets: ["F-Assured qualification", "PLA & PCA campaigns", "Brand Stories", "Big Billion Days prep"],
  },
  Meesho: {
    badge: "1.4M+ sellers",
    why: "India's largest seller base and tier-2/3 social commerce engine. Pricing and reseller-friendly imagery win here.",
    bullets: ["Reseller-optimised listings", "SKU velocity hacks", "Returns control", "Meesho Ads"],
  },
  JioMart: {
    badge: "18,000+ stores",
    why: "Reliance's omni-channel platform. Unmatched tier-2/3 reach for FMCG, grocery, home and fashion.",
    bullets: ["JioMart Fulfilled setup", "Catalog template mastery", "Bundle pricing", "Festive deal submission"],
  },
  Ajio: {
    badge: "Premium fashion",
    why: "Reliance's fashion & lifestyle marketplace. Strong AOV, premium positioning, curated brand mix.",
    bullets: ["Brand approval", "Lookbook-style catalog", "Returns minimisation", "Reliance Trends crossover"],
  },
};

export default function Marketplaces() {
  return (
    <>
      <SEO
        title="Marketplaces — Amazon, Flipkart, Meesho, JioMart & Ajio"
        description="Sell on Amazon, Flipkart, Meesho, JioMart and Ajio with Orina's expert onboarding and account management. India's leading marketplace agency."
        path="/marketplaces"
      />

      <section className="border-b-2 border-black bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="tape">Marketplaces</div>
          <h1 className="display mt-6 max-w-4xl text-5xl font-black leading-[0.95] tracking-tight md:text-6xl lg:text-7xl">
            India's biggest marketplaces. <span className="bg-[#FFD700] px-2">One team to run them.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base text-black/70 md:text-lg">
            Each marketplace plays by different rules. We've shipped on all of them — and we know what wins.
          </p>
        </div>
      </section>

      <section className="bg-[#FAFAF7] py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="space-y-8">
            {MARKETPLACES.map((m, i) => {
              const d = DETAILS[m.name];
              return (
                <div
                  key={m.name}
                  data-testid={`marketplace-${m.name.toLowerCase()}`}
                  className="card-brutal grid grid-cols-1 items-center gap-8 p-8 md:grid-cols-12 md:p-12"
                >
                  <div className="md:col-span-4">
                    <div className="display text-5xl font-black md:text-6xl" style={{ color: m.color }}>
                      {m.name}
                    </div>
                    <div className="mt-3 inline-block border-2 border-black bg-[#FFD700] px-3 py-1 text-xs font-bold uppercase tracking-widest">
                      {d?.badge}
                    </div>
                  </div>
                  <div className="md:col-span-5">
                    <p className="text-base leading-relaxed text-black/80 md:text-lg">{d?.why}</p>
                    <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {d?.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="md:col-span-3 md:text-right">
                    <Link
                      to={`/contact/?type=onboard&mp=${m.name}`}
                      className="btn-brutal"
                      data-testid={`marketplace-cta-${m.name.toLowerCase()}`}
                    >
                      Onboard on {m.name} <ArrowUpRight size={16} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <PlatformLinks groups={["marketplaces"]} title="Marketplace services" />

      <CtaBanner
        title="Want to launch on multiple marketplaces?"
        subtitle="One team, one Slack channel, one weekly report — across every channel that matters."
      />
    </>
  );
}
